// Luxe · Project Summary Strip — premium one-line overview that sits
// above the six-stage navigation. Client-safe by design: in Client
// View mode the internal Investment figure is suppressed and only the
// approved/client-facing investment is ever shown.
import { formatAUD } from "@/lib/api";

const STAGE_LABEL = {
  brief:     "Brief",
  design:    "Design",
  visualise: "Visualise",
  price:     "Price",
  proposal:  "Proposal",
  approval:  "Approval",
};

const PROPOSAL_STATUS_LABEL = {
  draft:     "Draft",
  sent:      "Sent to client",
  viewed:    "Viewed by client",
  accepted:  "Accepted",
  declined:  "Declined",
  superseded: "Superseded",
  revision_requested: "Changes requested",
};

function pickCurrentDesignName(sceneVersions, activeOptionId) {
  const opts = (sceneVersions || []).filter((s) => !s.archived_at);
  if (activeOptionId) {
    const found = opts.find((o) => o.id === activeOptionId);
    if (found?.name) return found.name;
  }
  const approved = opts.find((o) => o.status === "approved");
  if (approved?.name) return `${approved.name} · Approved`;
  if (opts.length > 0) return opts[opts.length - 1].name || `Design V${opts.length}`;
  return null;
}

function latestProposalStatus(proposals) {
  const versions = (proposals || []).flatMap((p) => p.versions || []);
  if (!versions.length) return null;
  const ordered = [...versions].sort((a, b) => (b.version || 0) - (a.version || 0));
  const s = ordered[0].status;
  return PROPOSAL_STATUS_LABEL[s] || s;
}

function acceptedInvestment(proposals) {
  const versions = (proposals || []).flatMap((p) => p.versions || []);
  const accepted = versions.filter((v) => v.status === "accepted");
  if (!accepted.length) return null;
  const cents = accepted[0].totals_cents?.total;
  return typeof cents === "number" ? cents / 100 : null;
}

export default function ProjectSummaryStrip({
  project,
  sceneVersions,
  proposals,
  activeOptionId,
  currentStage,
  investmentTotal,
  clientView = false,
}) {
  const client = project?.client;
  const clientName = client?.name || project?.client_name || null;
  const address = client?.address || null;
  const designName = pickCurrentDesignName(sceneVersions, activeOptionId);
  const proposalStatus = latestProposalStatus(proposals);
  const accepted = acceptedInvestment(proposals);

  // Client View safety: never expose the working investment total.
  // Only the client-approved figure (from a proposal in "accepted"
  // status) is safe to surface here.
  const value = clientView
    ? accepted
    : (accepted ?? (typeof investmentTotal === "number" ? investmentTotal : null));
  const valueLabel = clientView
    ? "Approved investment"
    : accepted ? "Approved investment" : "Current investment";

  const items = [
    { key: "client",   label: "Client",         value: clientName || "Not set" },
    { key: "address",  label: "Address",        value: address || "Not set" },
    { key: "stage",    label: "Current stage",  value: STAGE_LABEL[currentStage] || "—" },
    { key: "design",   label: "Current design", value: designName || "No saved design" },
    { key: "proposal", label: "Proposal",       value: proposalStatus || "No proposal yet" },
    { key: "value",    label: valueLabel,       value: value != null ? formatAUD(value) : "—" },
  ];

  return (
    <section data-testid="project-summary-strip"
             className="luxe-card px-5 py-4 mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3">
        {items.map((it, idx) => (
          <div key={it.key}
               data-testid={`project-summary-${it.key}`}
               className={`min-w-0 ${idx > 0 ? "lg:border-l lg:border-border/70 lg:pl-6" : ""}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{it.label}</div>
            <div className="text-sm font-medium mt-1 truncate" title={it.value}>{it.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
